import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { QUEUE_PLATFORMS } from '../../../../utils/sales/sales.constants';
import InputDropdown from '../../../../components/input-dropdown/InputDropdown';
import InputBar from '../../../../components/input-bar/InputBar';
import { SelectAllContainer, SelectionCount } from '../../../ebay/EbayPage.styles';
import EbayPage from '../../../ebay/EbayPage';
import WhatnotPage from '../../../whatnot/whatnot-backend/WhatnotPage';
import DiscordQueue from './DiscordQueue';

const QueueToolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
`;

const QueueSearch = styled.div`
    flex: 1 1 auto;
    max-width: 360px;
    min-width: 160px;
`;

const QueueTotal = styled.div`
    flex: 0 0 148px;
    width: 148px;
`;

const formatQueueTotal = (amount) => new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
}).format(amount || 0);

const EMPTY_SELECTION = { allSelected: false, disabled: true, toggle: () => {} };

export default function TransactionQueue() {
    const [params, setParams] = useSearchParams();
    const requested = params.get('platform');
    const platform = QUEUE_PLATFORMS.includes(requested) ? requested : 'eBay';

    const [playerSearch, setPlayerSearch] = useState('');
    const [selection, setSelection] = useState(EMPTY_SELECTION);
    const [listTotal, setListTotal] = useState(0);

    const setPlatform = (value) => {
        setPlayerSearch('');
        setSelection(EMPTY_SELECTION);
        setListTotal(0);
        setParams({ platform: value });
    };

    const handleSelectionChange = useCallback((next) => {
        setSelection(next);
    }, []);

    const handleListTotalChange = useCallback((total) => {
        setListTotal(total);
    }, []);

    return (
        <div>
            <QueueToolbar>
                <InputDropdown
                    id="queue-platform"
                    label="Platform"
                    value={platform}
                    onChange={setPlatform}
                    options={QUEUE_PLATFORMS}
                />
                <SelectAllContainer>
                    <input
                        type="checkbox"
                        id="queue-select-all"
                        checked={selection.allSelected}
                        disabled={selection.disabled}
                        onChange={selection.toggle}
                        style={{ accentColor: 'rgba(255,255,255,0.85)', cursor: 'pointer', height: 15, width: 15 }}
                    />
                    <SelectionCount as="label" htmlFor="queue-select-all" style={{ cursor: 'pointer' }}>
                        {selection.allSelected ? 'Deselect all' : 'Select all'}
                    </SelectionCount>
                </SelectAllContainer>
                <QueueSearch>
                    <InputBar
                        id="queue-player-search"
                        type="text"
                        label="Search player name..."
                        value={playerSearch}
                        updateValue={setPlayerSearch}
                        expands={false}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                setPlayerSearch('');
                                e.target.blur();
                            }
                        }}
                    />
                </QueueSearch>
                <QueueTotal>
                    <InputBar
                        id="queue-list-total"
                        type="text"
                        label="Total"
                        value={formatQueueTotal(listTotal)}
                        updateValue={() => {}}
                        readOnly
                        prefix="$"
                        aria-label="Total list value"
                    />
                </QueueTotal>
            </QueueToolbar>

            {platform === 'eBay' && (
                <EbayPage
                    playerSearch={playerSearch}
                    onSelectionChange={handleSelectionChange}
                    onListTotalChange={handleListTotalChange}
                />
            )}
            {platform === 'Whatnot' && (
                <WhatnotPage
                    embedded
                    playerSearch={playerSearch}
                    onSelectionChange={handleSelectionChange}
                    onListTotalChange={handleListTotalChange}
                />
            )}
            {platform === 'Discord' && (
                <DiscordQueue
                    playerSearch={playerSearch}
                    onSelectionChange={handleSelectionChange}
                    onListTotalChange={handleListTotalChange}
                />
            )}
        </div>
    );
}
